import React, { useState, useEffect, useRef } from 'react';
import { useAuthContext } from '../context/AuthContext';
import { useTheme } from '../../../context/ThemeContext';
import toast from 'react-hot-toast';
import UserSearch from './UserSearch';
import PartnerInviteNotification from '../../notifications/components/PartnerInviteNotification';
import ChangePasswordModal from './ChangePasswordModal';
import { Dialog } from '@headlessui/react';
import { Link } from 'react-router-dom';
import { XCircleIcon, CheckCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import ThemeSelector from '../../../components/shared/ThemeSelector';

/**
 * Format phone number as (XXX) XXX-XXXX
 */
const formatPhoneNumber = (value) => {
  if (!value) return '';
  const phoneNumber = value.replace(/\D/g, '');
  const match = phoneNumber.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
  if (!match) return value;
  return !match[2] ? match[1] 
    : `(${match[1]}) ${match[2]}${match[3] ? `-${match[3]}` : ''}`;
};

/**
 * Clean phone number to just digits
 */
const cleanPhoneNumber = (value) => {
  return value.replace(/\D/g, '');
};

/**
 * Get minimum date for date picker (115 years ago from today)
 */
const getMinDate = () => {
  const today = new Date();
  const minDate = new Date(today.getFullYear() - 115, today.getMonth(), today.getDate());
  return minDate.toISOString().split('T')[0];
};

/**
 * Get maximum date for date picker (18 years ago from today)
 */
const getMaxDate = () => {
  const today = new Date();
  const maxDate = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
  return maxDate.toISOString().split('T')[0];
};

// Helper function to safely compare MongoDB IDs which may be in different formats
const compareIds = (id1, id2) => {
  if (!id1 || !id2) return false;
  return String(id1) === String(id2);
};

const Profile = () => {
  const { user, updateUser, getPartnerInvitations, refreshUserData, createPartnerProfile, removePartner, updatePartnerProfile, generatePartnerInvite, rejectPartnerInvite, acceptPartnerInvite } = useAuthContext();
  const { themeColors } = useTheme();
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showCreatePartnerModal, setShowCreatePartnerModal] = useState(false);
  const [partnerData, setPartnerData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    dateOfBirth: '',
  });
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    dateOfBirth: '',
  });
  
  // Add state for onboarding profile data
  const [onboardingProfileData, setOnboardingProfileData] = useState({
    relationshipStatus: '',
    numberOfChildren: 0,
    financialGoals: [],
    riskTolerance: ''
  });
  
  const [sentInvitations, setSentInvitations] = useState([]);
  const [timeRemaining, setTimeRemaining] = useState({});
  const [expandedInvitation, setExpandedInvitation] = useState(null);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(null);
  const fileInputRef = useRef(null);

  // Populate profile data when user data is available
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        if (user) {
          console.log('👤 User data in Profile:', user);
          console.log('📊 Profile fields (main):', {
            relationshipStatus: user.relationshipStatus,
            numberOfChildren: user.numberOfChildren,
            financialGoals: user.financialGoals,
            riskTolerance: user.riskTolerance,
            onboardingCompleted: user.onboardingCompleted
          });
          console.log('📊 Profile fields (onboarding):', {
            onboardingData: user.onboarding,
            profileData: user.onboarding?.profileData,
            relationshipStatus: user.onboarding?.profileData?.relationshipStatus,
            numberOfChildren: user.onboarding?.profileData?.numberOfChildren,
            financialGoals: user.onboarding?.profileData?.financialGoals,
            riskTolerance: user.onboarding?.profileData?.riskTolerance
          });
          
          // If phone number or DOB are missing, try to refresh user data
          if (!user.phoneNumber || !user.dateOfBirth) {
            const updatedUser = await refreshUserData();
            console.log('🔄 Refreshed user data:', updatedUser);
          }
          
          setProfileData({
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            email: user.email || '',
            phoneNumber: user.phoneNumber ? formatPhoneNumber(user.phoneNumber) : '',
            dateOfBirth: user.dateOfBirth ? new Date(user.dateOfBirth).toISOString().split('T')[0] : '',
          });
          
          // Set onboarding profile data from main fields or onboarding data
          const relationshipStatus = user?.relationshipStatus || user?.onboarding?.profileData?.relationshipStatus || '';
          const numberOfChildren = user?.numberOfChildren !== undefined ? user.numberOfChildren : user?.onboarding?.profileData?.numberOfChildren || 0;
          const financialGoals = user?.financialGoals?.length > 0 ? user.financialGoals : user?.onboarding?.profileData?.financialGoals || [];
          const riskTolerance = user?.riskTolerance || user?.onboarding?.profileData?.riskTolerance || '';
          
          setOnboardingProfileData({
            relationshipStatus,
            numberOfChildren,
            financialGoals,
            riskTolerance
          });
        }
      } catch (error) {
        console.error('❌ Error fetching user data:', error);
        toast.error('Failed to load user profile data');
      }
    };
    
    fetchUserData();
  }, [user, refreshUserData]);

  // Fetch partner invitations
  useEffect(() => {
    const fetchInvitations = async () => {
      try {
        setLoading(true);
        const response = await getPartnerInvitations();
        console.log('🔍 DEBUG - Fetched invitations in Profile:', response);
        
        // Check if invitations exist in the response
        if (!response.invitations || !Array.isArray(response.invitations)) {
          console.error('No invitations array in API response:', response);
          setInvitations([]);
          setSentInvitations([]);
          setLoading(false);
          return;
        }
        
        // Log a sample invitation for debugging
        if (response.invitations.length > 0) {
          console.log('Sample invitation data:', response.invitations[0]);
          console.log('Current user id:', user.id);
        }
        
        // Get current user info for comparison
        const currentUserId = user.id;
        const currentUserEmail = user.email ? user.email.toLowerCase() : null;
        
        console.log('🔍 DEBUG - Current user info:', { id: currentUserId, email: currentUserEmail });
        
        // Process SENT invitations - invitations where current user is the sender
        const userSentInvitations = response.invitations.filter(inv => {
          // For debugging
          if (inv.sender) {
            console.log('Comparing invitation sender:', 
              typeof inv.sender === 'object' ? inv.sender._id : inv.sender, 
              'with current user:', currentUserId);
          }
          
          // Check if invitation has a sender that matches current user
          if (inv.sender && typeof inv.sender === 'object') {
            return compareIds(inv.sender._id, currentUserId);
          }
          
          // Check if invitation has a sender ID that matches current user
          if (inv.sender && typeof inv.sender === 'string') {
            return compareIds(inv.sender, currentUserId);
          }
          
          // Also check senderEmail if available
          if (inv.senderEmail && currentUserEmail) {
            return inv.senderEmail.toLowerCase() === currentUserEmail;
          }
          
          return false;
        });
        
        // Process RECEIVED invitations - invitations where current user is the recipient
        const receivedInvitations = response.invitations.filter(inv => {
          // For debugging
          if (inv.recipient) {
            console.log('Comparing invitation recipient:', 
              typeof inv.recipient === 'object' ? inv.recipient._id : inv.recipient, 
              'with current user:', currentUserId);
          }
          
          // Check if invitation has a recipient that matches current user
          if (inv.recipient && typeof inv.recipient === 'object') {
            return compareIds(inv.recipient._id, currentUserId);
          }
          
          // Check if invitation has a recipient ID that matches current user
          if (inv.recipient && typeof inv.recipient === 'string') {
            return compareIds(inv.recipient, currentUserId);
          }
          
          // Check recipient email (for invitations sent via email)
          if (inv.recipientEmail && currentUserEmail) {
            return inv.recipientEmail.toLowerCase() === currentUserEmail;
          }
          
          return false;
        });
        
        console.log('🔍 DEBUG - Filtered invitations:', { 
          received: receivedInvitations.length,
          sent: userSentInvitations.length,
          allInvitations: response.invitations.length
        });
        
        console.log('🔍 DEBUG - All invitations:', response.invitations);
        console.log('🔍 DEBUG - Received invitations:', receivedInvitations);
        console.log('🔍 DEBUG - Sent invitations:', userSentInvitations);
        
        setSentInvitations(userSentInvitations);
        setInvitations(receivedInvitations);
      } catch (error) {
        console.error('Failed to fetch invitations:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
    fetchInvitations();
    }
  }, [getPartnerInvitations, user]);

  // Update time remaining for invitations every minute
  useEffect(() => {
    const calculateTimeRemaining = () => {
      const remaining = {};
      sentInvitations.forEach(invitation => {
        const expiresAt = new Date(invitation.expiresAt);
        const now = new Date();
        const diffMs = expiresAt - now;
        
        if (diffMs <= 0) {
          remaining[invitation._id] = { 
            text: 'Expired',
            status: 'expired',
            timeMs: 0
          };
        } else if (diffMs < 3600000) { // less than an hour
          const minutes = Math.floor(diffMs / 60000);
          remaining[invitation._id] = {
            text: `${minutes} minute${minutes !== 1 ? 's' : ''} left`,
            status: minutes < 15 ? 'critical' : 'warning',
            timeMs: diffMs
          };
        } else if (diffMs < 86400000) { // less than a day
          const hours = Math.floor(diffMs / 3600000);
          remaining[invitation._id] = {
            text: `${hours} hour${hours !== 1 ? 's' : ''} left`,
            status: hours < 6 ? 'warning' : 'normal',
            timeMs: diffMs
          };
        } else { // more than a day
          const days = Math.floor(diffMs / 86400000);
          remaining[invitation._id] = {
            text: `${days} day${days !== 1 ? 's' : ''} left`,
            status: 'normal',
            timeMs: diffMs
          };
        }
      });
      
      setTimeRemaining(remaining);
    };
    
    // Calculate immediately and then every minute
    calculateTimeRemaining();
    const interval = setInterval(calculateTimeRemaining, 60000);
    
    return () => clearInterval(interval);
  }, [sentInvitations]);

  // Format date/time to display
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Toggle expanded view for an invitation
  const toggleExpand = (invitationId) => {
    if (expandedInvitation === invitationId) {
      setExpandedInvitation(null);
    } else {
      setExpandedInvitation(invitationId);
    }
  };

  // Handle resending an invitation
  const handleResendInvitation = async (invitationId) => {
    try {
      const recipientEmail = sentInvitations.find(inv => inv._id === invitationId)?.recipientEmail;
      if (!recipientEmail) {
        toast.error('Recipient email not found');
        return;
      }
      
      await generatePartnerInvite(recipientEmail);
      toast.success('Invitation resent successfully');
      
      // Refresh invitations
      const response = await getPartnerInvitations();
      if (response.invitations && Array.isArray(response.invitations)) {
        const userSentInvitations = response.invitations.filter(inv => {
          const invSenderId = inv.sender;
          const invSenderEmail = inv.senderEmail ? inv.senderEmail.toLowerCase() : null;
          const currentUserId = user.id;
          const currentUserEmail = user.email ? user.email.toLowerCase() : null;
          
          return (invSenderId && invSenderId === currentUserId) || 
                (currentUserEmail && invSenderEmail === currentUserEmail);
        });
        setSentInvitations(userSentInvitations);
      }
    } catch (error) {
      console.error('Failed to resend invitation', error);
      
      // Check if the error response indicates the invitation was actually sent
      if (error.response?.data?.success || 
          error.message?.includes('rate limit') ||
          error.message?.includes('trust proxy')) {
        toast.success('Invitation resent successfully');
      } else {
        toast.error('Failed to resend invitation');
      }
    }
  };
  
  // Handle revoking an invitation
  const handleRevokeInvitation = async (invitationId) => {
    try {
      await rejectPartnerInvite(invitationId);
      
      // Remove from local state
      setSentInvitations(sentInvitations.filter(inv => inv._id !== invitationId));
      setShowRevokeConfirm(null);
      
      toast.success('Invitation revoked successfully');
    } catch (error) {
      console.error('Failed to revoke invitation', error);
      toast.error('Failed to revoke invitation');
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      setLoading(true);
      
      // Validate phone number before submitting
      const cleanedPhone = cleanPhoneNumber(profileData.phoneNumber);
      if (cleanedPhone && cleanedPhone.length !== 10) {
        toast.error('Phone number must be 10 digits');
        setLoading(false);
        return;
      }
      
      // Validate date of birth
      if (!profileData.dateOfBirth) {
        toast.error('Please enter your date of birth');
        setLoading(false);
        return;
      }
      
      // Clean up data before sending to server
      const updatedData = {
        ...profileData,
        phoneNumber: cleanedPhone || profileData.phoneNumber,
        dateOfBirth: new Date(profileData.dateOfBirth).toISOString(),
        // Include onboarding profile data
        relationshipStatus: onboardingProfileData.relationshipStatus || '',
        numberOfChildren: onboardingProfileData.numberOfChildren || 0,
        financialGoals: onboardingProfileData.financialGoals || [],
        riskTolerance: onboardingProfileData.riskTolerance || ''
      };
      
      console.log('🔄 Updating profile with data:', updatedData);
      
      const response = await updateUser(updatedData);
      
      console.log('✅ Profile update response:', response);
      
      // Update local state immediately with response data
      if (response && response.user) {
        const updatedUser = response.user;
        
        // Update profile data state
        setProfileData({
          firstName: updatedUser.firstName || profileData.firstName,
          lastName: updatedUser.lastName || profileData.lastName,
          email: updatedUser.email || profileData.email,
          phoneNumber: updatedUser.phoneNumber ? formatPhoneNumber(updatedUser.phoneNumber) : profileData.phoneNumber,
          dateOfBirth: updatedUser.dateOfBirth ? new Date(updatedUser.dateOfBirth).toISOString().split('T')[0] : profileData.dateOfBirth,
        });
        
        // Update onboarding profile data state
        setOnboardingProfileData({
          relationshipStatus: updatedUser.relationshipStatus || '',
          numberOfChildren: updatedUser.numberOfChildren !== undefined ? updatedUser.numberOfChildren : 0,
          financialGoals: updatedUser.financialGoals || [],
          riskTolerance: updatedUser.riskTolerance || ''
        });
      }
      
      setIsEditing(false);
      
      // Show success message
      toast.success('Profile updated successfully! Your changes are now saved.');
      
      // Refresh user data to ensure UI reflects latest state
      // The state has already been updated above, but refresh to sync with context
      const refreshedUser = await refreshUserData();
      
      // Ensure state is updated with refreshed data (in case refreshUserData returns different data)
      if (refreshedUser) {
        setProfileData({
          firstName: refreshedUser.firstName || profileData.firstName,
          lastName: refreshedUser.lastName || profileData.lastName,
          email: refreshedUser.email || profileData.email,
          phoneNumber: refreshedUser.phoneNumber ? formatPhoneNumber(refreshedUser.phoneNumber) : profileData.phoneNumber,
          dateOfBirth: refreshedUser.dateOfBirth ? new Date(refreshedUser.dateOfBirth).toISOString().split('T')[0] : profileData.dateOfBirth,
        });
        
        setOnboardingProfileData({
          relationshipStatus: refreshedUser.relationshipStatus || '',
          numberOfChildren: refreshedUser.numberOfChildren !== undefined ? refreshedUser.numberOfChildren : 0,
          financialGoals: refreshedUser.financialGoals || [],
          riskTolerance: refreshedUser.riskTolerance || ''
        });
      }
      
    } catch (error) {
      console.error('❌ Failed to update profile:', error);
      
      // Show specific error message if available
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.errors?.[0]?.msg ||
                          error.response?.data?.errors?.[0] ||
                          'Failed to update profile. Please try again.';
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneNumberChange = (e) => {
    const formattedNumber = formatPhoneNumber(e.target.value);
    setProfileData({ ...profileData, phoneNumber: formattedNumber });
  };

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        // TODO: Implement profile picture upload
        toast.success('Profile picture updated');
      } catch (error) {
        toast.error('Failed to upload profile picture');
      }
    }
  };

  const handleInvitationAction = async (invitationId, action) => {
    try {
      if (action === 'accept') {
        await acceptPartnerInvite(invitationId, false); // false means it's an ID, not a token
        toast.success('Invitation accepted successfully');
      } else if (action === 'reject') {
        await rejectPartnerInvite(invitationId);
        toast.success('Invitation rejected successfully');
      }
      
      // Refresh user data to update partner status and remove from invitations
      await refreshUserData();
      await fetchInvitations();
    } catch (error) {
      console.error(`Failed to ${action} invitation:`, error);
      toast.error(`Failed to ${action} invitation`);
    }
  };

  const handleInviteSent = async () => {
    try {
      const response = await getPartnerInvitations();
      console.log('Fetched invitations after sending in Profile:', response);
      
      // Check if invitations exist in the response
      if (!response.invitations || !Array.isArray(response.invitations)) {
        console.error('No invitations array in API response after sending:', response);
        setInvitations([]);
        setSentInvitations([]);
        return;
      }
      
      // Log a sample invitation for debugging
      if (response.invitations.length > 0) {
        console.log('Sample invitation data:', response.invitations[0]);
        console.log('Current user id:', user.id);
      }
      
      // Get current user info for comparison
      const currentUserId = user.id;
      const currentUserEmail = user.email ? user.email.toLowerCase() : null;
      
      console.log('Current user for filtering:', { id: currentUserId, email: currentUserEmail });
      
      // Process SENT invitations - invitations where current user is the sender
      const userSentInvitations = response.invitations.filter(inv => {
        // For debugging
        if (inv.sender) {
          console.log('Comparing invitation sender:', 
            typeof inv.sender === 'object' ? inv.sender._id : inv.sender, 
            'with current user:', currentUserId);
        }
        
        // Check if invitation has a sender that matches current user
        if (inv.sender && typeof inv.sender === 'object') {
          return compareIds(inv.sender._id, currentUserId);
        }
        
        // Check if invitation has a sender ID that matches current user
        if (inv.sender && typeof inv.sender === 'string') {
          return compareIds(inv.sender, currentUserId);
        }
        
        // Also check senderEmail if available
        if (inv.senderEmail && currentUserEmail) {
          return inv.senderEmail.toLowerCase() === currentUserEmail;
        }
        
        return false;
      });
      
      console.log('Filtered sent invitations:', userSentInvitations);
      
      // Process RECEIVED invitations - invitations where current user is the recipient
      const receivedInvitations = response.invitations.filter(inv => {
        // For debugging
        if (inv.recipient) {
          console.log('Comparing invitation recipient:', 
            typeof inv.recipient === 'object' ? inv.recipient._id : inv.recipient, 
            'with current user:', currentUserId);
        }
        
        // Check if invitation has a recipient that matches current user
        if (inv.recipient && typeof inv.recipient === 'object') {
          return compareIds(inv.recipient._id, currentUserId);
        }
        
        // Check if invitation has a recipient ID that matches current user
        if (inv.recipient && typeof inv.recipient === 'string') {
          return compareIds(inv.recipient, currentUserId);
        }
        
        // Check recipient email (for invitations sent via email)
        if (inv.recipientEmail && currentUserEmail) {
          return inv.recipientEmail.toLowerCase() === currentUserEmail;
        }
        
        return false;
      });
      
      console.log('Filtered received invitations:', receivedInvitations);
      
      setSentInvitations(userSentInvitations);
      setInvitations(receivedInvitations);
      toast.success('Invitation sent successfully');
    } catch (error) {
      console.error('Failed to refresh invitations:', error);
      toast.error('Failed to refresh invitations');
    }
  };

  const handleCreatePartner = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      // Clean up data before sending to server
      const partnerInfo = {
        ...partnerData,
        phoneNumber: cleanPhoneNumber(partnerData.phoneNumber),
        dateOfBirth: partnerData.dateOfBirth ? new Date(partnerData.dateOfBirth).toISOString() : null,
      };
      
      if (user?.isCreatedPartner) {
        // Update existing partner profile
        await updatePartnerProfile(partnerInfo);
        toast.success('Partner profile updated successfully');
      } else {
        // Create new partner profile
        await createPartnerProfile(partnerInfo);
        toast.success('Partner profile created successfully');
      }
      
      setShowCreatePartnerModal(false);
      setPartnerData({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        dateOfBirth: '',
      });
      
    } catch (error) {
      console.error('Failed to manage partner profile:', error);
      toast.error(user?.isCreatedPartner ? 'Failed to update partner profile' : 'Failed to create partner profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePartnerPhoneNumberChange = (e) => {
    const formattedNumber = formatPhoneNumber(e.target.value);
    setPartnerData({ ...partnerData, phoneNumber: formattedNumber });
  };

  const handleRemovePartner = async () => {
    if (window.confirm('Are you sure you want to remove your partner?')) {
      try {
        setLoading(true);
        await removePartner();
        toast.success('Partner removed successfully');
      } catch (error) {
        console.error('Failed to remove partner:', error);
        toast.error('Failed to remove partner');
      } finally {
        setLoading(false);
      }
    }
  };

  return (<>
    <div className="max-w-4xl mx-auto px-4 py-8 min-h-screen" style={{
      background: `linear-gradient(to bottom, var(--bg-gradient-from, #BFDBFE), var(--bg-gradient-via, rgba(147, 197, 253, 0.5)), var(--bg-gradient-to, rgba(168, 85, 247, 0.3)))`
    }}>
      <div className="mb-8 flex items-center justify-between">
        <h1 
          className="text-2xl font-semibold"
          style={{ color: themeColors.primaryDark }}
        >
          Profile Settings
        </h1>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="px-6 py-2 text-white rounded-full text-sm font-medium transition-colors"
            style={{ 
              backgroundColor: themeColors.primary,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = themeColors.primaryDark;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = themeColors.primary;
            }}
          >
            Edit Profile
          </button>
        )}
      </div>
      
      {/* Profile Picture & Basic Info */}
      <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-sm p-8 mb-8">
        <div className="flex items-start space-x-6">
          <div className="relative">
            <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden">
              {user?.profilePicture ? (
                <img
                  src={user.profilePicture}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-4xl text-gray-400">
                  {profileData.firstName?.[0]?.toUpperCase() || 'T'}
                </span>
              )}
            </div>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="absolute bottom-0 right-0 text-white p-1.5 rounded-full transition-colors"
              style={{ backgroundColor: themeColors.primary }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = themeColors.primaryDark;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = themeColors.primary;
              }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
            </button>
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="image/*"
              onChange={handleProfilePictureUpload}
            />
          </div>

          <form onSubmit={handleProfileUpdate} className="flex-1 space-y-6" noValidate>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-2">First Name</label>
                <input
                  type="text"
                  value={profileData.firstName}
                  onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
                  disabled={!isEditing}
                  className="w-full px-4 py-3 bg-white text-black border border-gray-200 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                  style={{ '--focus-ring-color': themeColors.primary }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = themeColors.primary;
                    e.currentTarget.style.boxShadow = `0 0 0 2px ${themeColors.interactive.focusRing}`;
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#E5E7EB';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  placeholder="Enter first name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-2">Last Name</label>
                <input
                  type="text"
                  value={profileData.lastName}
                  onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
                  disabled={!isEditing}
                  className="w-full px-4 py-3 bg-white text-black border border-gray-200 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                  style={{ '--focus-ring-color': themeColors.primary }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = themeColors.primary;
                    e.currentTarget.style.boxShadow = `0 0 0 2px ${themeColors.interactive.focusRing}`;
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#E5E7EB';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  placeholder="Enter last name"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">Email</label>
              <div className="flex items-center space-x-2">
                <input
                  type="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                  disabled={!isEditing}
                  className="flex-1 px-4 py-3 bg-white text-black border border-gray-200 rounded-xl transition-colors"
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = themeColors.primary;
                    e.currentTarget.style.boxShadow = `0 0 0 2px ${themeColors.interactive.focusRing}`;
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#E5E7EB';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  placeholder="Enter email address"
                />
                {!user?.isEmailVerified && (
                  <button
                    type="button"
                    className="px-4 py-2 text-sm text-white rounded-xl transition-colors"
                    style={{ backgroundColor: themeColors.primary }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = themeColors.primaryDark;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = themeColors.primary;
                    }}
                  >
                    Verify Email
                  </button>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">Phone Number</label>
              <input
                type="tel"
                value={profileData.phoneNumber}
                onChange={handlePhoneNumberChange}
                disabled={!isEditing}
                className="w-full px-4 py-3 bg-white text-black border border-gray-200 rounded-xl transition-colors"
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = themeColors.primary;
                  e.currentTarget.style.boxShadow = `0 0 0 2px ${themeColors.interactive.focusRing}`;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#E5E7EB';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                placeholder="(XXX) XXX-XXXX"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">Date of Birth</label>
              <input
                type="date"
                value={profileData.dateOfBirth}
                onChange={(e) => setProfileData({ ...profileData, dateOfBirth: e.target.value })}
                disabled={!isEditing}
                min={getMinDate()}
                max={getMaxDate()}
                className="w-full px-4 py-3 bg-white text-black border border-gray-200 rounded-xl transition-colors"
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = themeColors.primary;
                  e.currentTarget.style.boxShadow = `0 0 0 2px ${themeColors.interactive.focusRing}`;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#E5E7EB';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </div>

            {isEditing && (
              <div className="flex space-x-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-6 py-3 text-white rounded-xl transition-colors"
                  style={{ backgroundColor: themeColors.primary }}
                  onMouseEnter={(e) => {
                    if (!loading) {
                      e.currentTarget.style.backgroundColor = themeColors.primaryDark;
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = themeColors.primary;
                  }}
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="px-6 py-3 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            )}
          </form>
        </div>
      </div>

      {/* Profile Information Section - Always Editable */}
      <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-sm p-8 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Profile Information</h3>
          <p className="text-xs text-gray-500">
            💡 This information helps us personalize your experience
          </p>
        </div>
        
        <form onSubmit={handleProfileUpdate} className="space-y-6" noValidate>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Relationship Status - Always Editable */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">Relationship Status</label>
              <div className="relative">
                <select
                  value={onboardingProfileData.relationshipStatus}
                  onChange={(e) => setOnboardingProfileData(prev => ({ ...prev, relationshipStatus: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl appearance-none bg-white text-gray-900 transition-colors"
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = themeColors.primary;
                    e.currentTarget.style.boxShadow = `0 0 0 2px ${themeColors.interactive.focusRing}`;
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#D1D5DB';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  onMouseEnter={(e) => {
                    if (document.activeElement !== e.currentTarget) {
                      e.currentTarget.style.borderColor = '#9CA3AF';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (document.activeElement !== e.currentTarget) {
                      e.currentTarget.style.borderColor = '#D1D5DB';
                    }
                  }}
                >
                  <option value="">Select status</option>
                  <option value="single">😊 Single</option>
                  <option value="married">💑 Married</option>
                  <option value="partner">👫 Partnership</option>
                  <option value="divorced">🙋 Divorced</option>
                  <option value="widowed">🤍 Widowed</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>
            
            {/* Number of Children - Always Editable */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">Number of Children</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-2xl">👶</span>
                </div>
                <input
                  type="number"
                  value={onboardingProfileData.numberOfChildren}
                  onChange={(e) => setOnboardingProfileData(prev => ({ ...prev, numberOfChildren: parseInt(e.target.value) || 0 }))}
                  min="0"
                  max="20"
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl text-gray-900 transition-colors"
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = themeColors.primary;
                    e.currentTarget.style.boxShadow = `0 0 0 2px ${themeColors.interactive.focusRing}`;
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#D1D5DB';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  onMouseEnter={(e) => {
                    if (document.activeElement !== e.currentTarget) {
                      e.currentTarget.style.borderColor = '#9CA3AF';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (document.activeElement !== e.currentTarget) {
                      e.currentTarget.style.borderColor = '#D1D5DB';
                    }
                  }}
                  placeholder="0"
                />
              </div>
            </div>
            
            {/* Risk Tolerance - Always Editable */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-600 mb-2">Investment Risk Tolerance</label>
              <div className="space-y-2">
                {[
                  { value: 'conservative', label: 'Conservative', icon: '🛡️', description: 'Prefer stable, low-risk investments' },
                  { value: 'moderate', label: 'Moderate', icon: '⚖️', description: 'Balanced mix of risk and stability' },
                  { value: 'aggressive', label: 'Aggressive', icon: '🚀', description: 'Comfortable with high-risk, high-reward' }
                ].map((option) => (
                  <label 
                    key={option.value} 
                    className="flex items-center p-3 border border-gray-200 rounded-xl cursor-pointer transition-colors"
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = themeColors.primary;
                      e.currentTarget.style.backgroundColor = themeColors.primary50;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#E5E7EB';
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <input
                      type="radio"
                      name="riskTolerance"
                      value={option.value}
                      checked={onboardingProfileData.riskTolerance === option.value}
                      onChange={(e) => setOnboardingProfileData(prev => ({ ...prev, riskTolerance: e.target.value }))}
                      className="sr-only"
                    />
                    <div 
                      className="w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center"
                      style={{
                        borderColor: onboardingProfileData.riskTolerance === option.value 
                          ? themeColors.primary 
                          : '#D1D5DB',
                        backgroundColor: onboardingProfileData.riskTolerance === option.value 
                          ? themeColors.primary 
                          : 'transparent'
                      }}
                    >
                      {onboardingProfileData.riskTolerance === option.value && (
                        <div className="w-2 h-2 rounded-full bg-white"></div>
                      )}
                    </div>
                    <span className="text-xl mr-3">{option.icon}</span>
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{option.label}</div>
                      <div className="text-sm text-gray-500">{option.description}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          
          {/* Financial Goals - Always Editable */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">Financial Goals (select all that apply)</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { id: 'emergency_fund', label: 'Emergency Fund', icon: '🛡️' },
                { id: 'pay_off_debt', label: 'Pay Off Debt', icon: '💳' },
                { id: 'save_for_house', label: 'Save for House', icon: '🏠' },
                { id: 'plan_retirement', label: 'Plan Retirement', icon: '🏖️' },
                { id: 'invest_growth', label: 'Investment Growth', icon: '📈' },
                { id: 'save_education', label: 'Education Fund', icon: '🎓' },
                { id: 'build_wealth', label: 'Build Wealth', icon: '💎' },
                { id: 'other', label: 'Other Goals', icon: '🎯' }
              ].map((goal) => (
                <label 
                  key={goal.id} 
                  className="flex flex-col items-center p-3 border border-gray-200 rounded-xl cursor-pointer transition-colors"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = themeColors.primary;
                    e.currentTarget.style.backgroundColor = themeColors.primary50;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#E5E7EB';
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <input
                    type="checkbox"
                    checked={onboardingProfileData.financialGoals.includes(goal.id)}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      setOnboardingProfileData(prev => ({
                        ...prev,
                        financialGoals: isChecked 
                          ? [...prev.financialGoals, goal.id]
                          : prev.financialGoals.filter(g => g !== goal.id)
                      }));
                    }}
                    className="sr-only"
                  />
                  <div 
                    className="w-6 h-6 rounded border-2 mb-2 flex items-center justify-center"
                    style={{
                      borderColor: onboardingProfileData.financialGoals.includes(goal.id) 
                        ? themeColors.primary 
                        : '#D1D5DB',
                      backgroundColor: onboardingProfileData.financialGoals.includes(goal.id) 
                        ? themeColors.primary 
                        : 'transparent'
                    }}
                  >
                    {onboardingProfileData.financialGoals.includes(goal.id) && (
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <span className="text-2xl mb-1">{goal.icon}</span>
                  <span className="text-xs text-center text-gray-700 font-medium">{goal.label}</span>
                </label>
              ))}
            </div>
          </div>
          
          {/* Save Button for Profile Information */}
          <div className="pt-4 border-t border-gray-200">
            <button
              type="submit"
              disabled={loading}
              className="w-full px-6 py-3 text-white rounded-xl font-medium transition-colors"
              style={{ backgroundColor: themeColors.primary }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.backgroundColor = themeColors.primaryDark;
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = themeColors.primary;
              }}
            >
              {loading ? 'Saving Profile Information...' : 'Save Profile Information'}
            </button>
          </div>
        </form>
      </div>

      {/* Theme Settings */}
      <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-sm p-8 mb-8">
        <ThemeSelector />
      </div>

      {/* Security Settings */}
      <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-sm p-8 mb-8">
        <h2 
          className="text-xl font-semibold mb-6"
          style={{ color: themeColors.primaryDark }}
        >
          Security Settings
        </h2>
        <div className="space-y-4">
          <button
            type="button"
            onClick={() => setShowPasswordModal(true)}
            className="w-full px-4 py-3 bg-white text-black flex items-center justify-between rounded-xl hover:bg-gray-100 border border-gray-200"
          >
            <span>Change Password</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <button
            type="button"
            className="w-full px-4 py-3 bg-white text-black flex items-center justify-between rounded-xl hover:bg-gray-100 border border-gray-200"
          >
            <span>Two-Factor Authentication</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Partner Management */}
      <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-sm p-8">
        <div className="flex items-center justify-between mb-4">
          <h2 
            className="text-xl font-semibold"
            style={{ color: themeColors.primaryDark }}
          >
            Partner Management
          </h2>
          <Link 
            to="/settings/partner" 
            className="px-4 py-2 text-white rounded-full text-sm font-medium flex items-center transition-opacity"
            style={{ backgroundColor: themeColors.primary }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.9';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
          >
            Partner Settings
            <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
        
        {user?.hasPartner ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <h3 className="text-lg font-medium text-gray-900">Current Partner</h3>
                <span className={`px-2 py-0.5 text-xs rounded-full ${user?.isCreatedPartner ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`}>
                  {user?.isCreatedPartner ? 'Created Profile' : 'Connected Account'}
                </span>
              </div>
              <button
                type="button"
                onClick={handleRemovePartner}
                className="text-red-600 hover:text-red-800 text-sm font-medium"
              >
                Remove
              </button>
            </div>
            
            <div className="flex items-start space-x-4 p-4 bg-purple-50 rounded-xl border border-purple-100">
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center text-white"
                style={{ backgroundColor: themeColors.primary }}
              >
                {user?.partner?.firstName?.[0]?.toUpperCase()}
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900">
                  {user?.partner?.firstName} {user?.partner?.lastName}
                </p>
                <p className="text-sm text-gray-500">{user?.partner?.email || 'No email provided'}</p>
                
                {user?.isCreatedPartner && (
                  <div className="mt-4 space-y-2">
                    {user?.partner?.phoneNumber && (
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">Phone:</span> {formatPhoneNumber(user?.partner?.phoneNumber)}
                      </p>
                    )}
                    
                    {user?.partner?.dateOfBirth && (
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">Date of Birth:</span> {new Date(user?.partner?.dateOfBirth).toLocaleDateString()}
                      </p>
                    )}
                    
                    <button
                      type="button"
                      onClick={() => {
                        // Populate partner data for editing
                        setPartnerData({
                          firstName: user?.partner?.firstName || '',
                          lastName: user?.partner?.lastName || '',
                          email: user?.partner?.email || '',
                          phoneNumber: user?.partner?.phoneNumber ? formatPhoneNumber(user?.partner?.phoneNumber) : '',
                          dateOfBirth: user?.partner?.dateOfBirth ? new Date(user?.partner?.dateOfBirth).toISOString().split('T')[0] : '',
                        });
                        setShowCreatePartnerModal(true);
                      }}
                      className="px-4 py-2 text-sm text-white rounded-xl transition-colors"
                      style={{ backgroundColor: themeColors.primary }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = themeColors.primaryDark;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = themeColors.primary;
                      }}
                    >
                      Edit Partner Profile
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-4">
            <p className="text-gray-600">
              Connect with your partner to share financial information and manage household expenses together.
            </p>
            
            <div className="flex flex-col md:flex-row md:space-x-4 space-y-4 md:space-y-0">
              <div className="flex-1">
                <h3 className="text-md font-medium mb-2 text-gray-900">Invite a Partner</h3>
                  <div className="flex">
                  <input
                      type="email"
                      id="partnerEmailInvite"
                      placeholder="Enter partner's email"
                      className="flex-grow px-4 py-3 bg-white text-black border border-gray-200 rounded-l-xl transition-colors"
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = themeColors.primary;
                        e.currentTarget.style.boxShadow = `0 0 0 2px ${themeColors.interactive.focusRing}`;
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = '#E5E7EB';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    />
                    <button
                      onClick={async () => {
                        const email = document.getElementById('partnerEmailInvite').value;
                        if (!email) {
                          toast.error('Please enter your partner\'s email address');
                          return;
                        }
                        try {
                          const response = await generatePartnerInvite(email);
                          console.log('Partner invite response:', response);
                          
                          // Check if the response contains a success message or has a success status
                          const isSuccess = response && 
                                           (response.success || 
                                            response.message?.toLowerCase().includes('success') ||
                                            response.status === 'success' ||
                                            response.status === 200);
                          
                          if (isSuccess) {
                            toast.success('Invitation sent successfully');
                            await handleInviteSent();
                            document.getElementById('partnerEmailInvite').value = '';
                          } else {
                            // If we have a specific error message, show it
                            if (response && response.message) {
                              toast.error(response.message);
                            } else {
                              toast.error('Failed to send invitation');
                            }
                          }
                        } catch (error) {
                          // Check if the error response indicates the invitation was actually sent
                          // Sometimes the rate limiter throws an error but the email is still sent
                          if (error.response?.data?.success || 
                              error.message?.includes('rate limit') ||
                              error.message?.includes('trust proxy')) {
                            toast.success('Invitation sent successfully');
                            await handleInviteSent();
                            document.getElementById('partnerEmailInvite').value = '';
                          } else {
                            console.error('Partner invitation error:', error);
                            toast.error(error.message || 'Failed to send invitation');
                          }
                        }
                      }}
                      className="px-4 py-3 text-white rounded-r-xl transition-colors"
                      style={{ backgroundColor: themeColors.primary }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = themeColors.primaryDark;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = themeColors.primary;
                      }}
                    >
                      Send Invitation
                    </button>
                </div>
              </div>
              
              <div className="flex-1">
                <h3 className="text-md font-medium mb-2 text-gray-900">Or Create a Partner Profile</h3>
                <button
                  type="button"
                  onClick={() => setShowCreatePartnerModal(true)}
                  className="w-full px-4 py-3 bg-gradient-to-r from-[var(--theme-primary)] to-[var(--theme-secondary)] text-[var(--text-on-primary)] rounded-xl hover:opacity-90 transition duration-200"
                >
                  Create Partner Profile
                </button>
              </div>
            </div>
          </div>

            {/* SENT INVITATIONS - Always visible when they exist */}
            {sentInvitations.length > 0 && (
              <div className="mt-6 p-4 bg-purple-50 rounded-xl border border-purple-100">
                <h3 className="text-md font-semibold mb-3 text-gray-900">Sent Invitations</h3>
                <div className="space-y-3">
                  {sentInvitations.map(invitation => {
                    const timeInfo = timeRemaining[invitation._id] || { status: 'unknown', text: 'Unknown' };
                    const isExpired = timeInfo.status === 'expired';
                    const recipientEmail = invitation.recipientEmail || 'Unknown recipient';
                    
                    return (
                      <div key={invitation._id} className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm">
                        <div className="flex justify-between items-center">
                          <div>
                            <div className="flex items-center space-x-2">
                              <span className={`px-2 py-0.5 text-xs rounded-full ${
                                isExpired ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
                              }`}>
                                {isExpired ? 'Expired' : 'Pending'}
                              </span>
                              <p className="text-sm font-medium text-gray-900">
                                {recipientEmail}
                              </p>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              Sent: {formatDateTime(invitation.createdAt)}
                            </p>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => setShowRevokeConfirm(invitation._id)}
                              className="text-xs px-2 py-1 text-red-600 border border-red-200 rounded hover:bg-red-50"
                            >
                              Cancel
                            </button>
                            {isExpired && (
                              <button
                                onClick={() => handleResendInvitation(invitation._id)}
                                className="text-xs px-2 py-1 text-blue-600 border border-blue-200 rounded hover:bg-blue-50"
                              >
                                Resend
                              </button>
                            )}
                          </div>
                        </div>
                        
                        {/* Revoke Confirmation */}
                        {showRevokeConfirm === invitation._id && (
                          <div className="mt-3 p-3 border border-red-100 bg-red-50 rounded-lg">
                            <p className="text-xs text-red-700 mb-2">
                              Are you sure you want to cancel this invitation?
                            </p>
                            <div className="flex justify-end space-x-2">
                              <button
                                onClick={() => setShowRevokeConfirm(null)}
                                className="text-xs px-2 py-1 bg-gray-200 rounded hover:bg-gray-300"
                              >
                                Keep
                              </button>
                              <button
                                onClick={() => handleRevokeInvitation(invitation._id)}
                                className="text-xs px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                              >
                                Cancel Invitation
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* RECEIVED INVITATIONS - Always visible when they exist */}
            {invitations.length > 0 && (
              <div className="mt-6 p-4 bg-green-50 rounded-xl border border-green-100">
                <h3 className="text-md font-semibold mb-3 text-gray-900">Received Invitations</h3>
                <div className="space-y-3">
                  {invitations
                    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) // Newest first
                    .map(invitation => {
                    const senderName = invitation.sender && typeof invitation.sender === 'object'
                      ? `${invitation.sender.firstName || ''} ${invitation.sender.lastName || ''}`.trim() || 'Partner'
                      : invitation.senderName || 'Partner';
                    
                    const senderEmail = invitation.sender && typeof invitation.sender === 'object'
                      ? invitation.sender.email
                      : invitation.senderEmail || '';

                      // Status detection logic matching Sharing Management modal
                      const isPending = invitation.status === 'PENDING';
                      const isAccepted = invitation.status === 'ACCEPTED';
                      const isCurrentPartnership = user?.partnerId && isAccepted;
                      const isDisconnected = !user?.partnerId && isAccepted;
                    
                    return (
                        <div 
                          key={invitation._id} 
                          className={`rounded-lg border p-3 shadow-sm ${
                            isDisconnected ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-200'
                          }`}
                        >
                        <div className="flex justify-between items-center">
                          <div>
                              <p className={`text-sm font-medium ${isDisconnected ? 'text-gray-500' : 'text-gray-900'}`}>
                              From: {senderName}
                            </p>
                            {senderEmail && (
                                <p className={`text-xs ${isDisconnected ? 'text-gray-400' : 'text-gray-500'}`}>
                                {senderEmail}
                              </p>
                            )}
                              <p className={`text-xs mt-1 ${isDisconnected ? 'text-gray-400' : 'text-gray-500'}`}>
                              Received: {formatDateTime(invitation.createdAt)}
                            </p>
                              {isAccepted && (
                                <p className={`text-xs mt-1 ${isDisconnected ? 'text-gray-400' : 'text-gray-500'}`}>
                                  Accepted: {formatDateTime(invitation.acceptedAt)}
                                </p>
                              )}
                              {isDisconnected && invitation.disconnectedAt && (
                                <p className="text-xs text-gray-400 mt-1">
                                  Disconnected: {formatDateTime(invitation.disconnectedAt)}
                                </p>
                              )}
                          </div>
                          
                          <div className="flex items-center space-x-2">
                              {isDisconnected ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                  <XCircleIcon className="h-3 w-3 mr-1" />
                                  Disconnected
                                </span>
                              ) : isPending ? (
                                <>
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                    <ClockIcon className="h-3 w-3 mr-1" />
                                    Pending
                                  </span>
                            <button
                              onClick={() => handleInvitationAction(invitation._id, 'reject')}
                              className="text-xs px-2 py-1 text-red-600 border border-red-200 rounded hover:bg-red-50"
                            >
                              Decline
                            </button>
                            <button
                              onClick={() => handleInvitationAction(invitation._id, 'accept')}
                              className="text-xs px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                            >
                              Accept
                            </button>
                                </>
                              ) : (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <CheckCircleIcon className="h-3 w-3 mr-1" />
                                  Connected
                                </span>
                              )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Debug Information - Admin Only */}
      {user?.isAdmin && (
        <div className="mt-6 p-4 bg-gray-100 rounded-lg">
          <h3 className="text-sm font-bold mb-2">Debug Information</h3>
          <div className="text-xs space-y-1">
            <p>Sent Invitations Count: {sentInvitations.length}</p>
            <p>Received Invitations Count: {invitations.length}</p>
            <p>Loading State: {loading ? 'Loading...' : 'Loaded'}</p>
            <p>Invitation IDs: {sentInvitations.map(inv => inv._id).join(', ') || 'None'}</p>
            <details>
              <summary className="cursor-pointer text-blue-600">Sent Invitations Data</summary>
              <pre className="mt-2 p-2 bg-gray-800 text-gray-100 overflow-auto rounded text-xs whitespace-pre-wrap">
                {JSON.stringify(sentInvitations, null, 2)}
              </pre>
            </details>
          </div>
        </div>
      )}

      </div>

      {/* Password Change Modal */}
      <ChangePasswordModal 
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
      />
      
      {/* Create/Edit Partner Modal */}
      <Dialog 
        open={showCreatePartnerModal} 
        onClose={() => setShowCreatePartnerModal(false)}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-md rounded-3xl bg-white p-8 shadow-xl">
            <Dialog.Title 
              className="text-xl font-semibold mb-6"
              style={{ color: themeColors.primaryDark }}
            >
              {user?.isCreatedPartner ? 'Update Partner Profile' : 'Create Partner Profile'}
            </Dialog.Title>
            
            <p className="text-gray-600 mb-6">
              {user?.isCreatedPartner 
                ? 'Update your partner\'s information below.'
                : 'Add your partner\'s information to create a profile within your account. This doesn\'t create a separate login for them.'}
            </p>
            
            <form onSubmit={handleCreatePartner} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">First Name</label>
                  <input
                    type="text"
                    value={partnerData.firstName}
                    onChange={(e) => setPartnerData({ ...partnerData, firstName: e.target.value })}
                    className="w-full px-4 py-3 bg-white text-black border border-gray-200 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                  style={{ '--focus-ring-color': themeColors.primary }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = themeColors.primary;
                    e.currentTarget.style.boxShadow = `0 0 0 2px ${themeColors.interactive.focusRing}`;
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#E5E7EB';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                    placeholder="Partner's first name"
                    required
                  />
                </div>
          <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">Last Name</label>
                  <input
                    type="text"
                    value={partnerData.lastName}
                    onChange={(e) => setPartnerData({ ...partnerData, lastName: e.target.value })}
                    className="w-full px-4 py-3 bg-white text-black border border-gray-200 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                  style={{ '--focus-ring-color': themeColors.primary }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = themeColors.primary;
                    e.currentTarget.style.boxShadow = `0 0 0 2px ${themeColors.interactive.focusRing}`;
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#E5E7EB';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                    placeholder="Partner's last name"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-2">Email (Optional)</label>
                <input
                  type="email"
                  value={partnerData.email}
                  onChange={(e) => setPartnerData({ ...partnerData, email: e.target.value })}
                  className="w-full px-4 py-3 bg-white text-black border border-gray-200 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                  style={{ '--focus-ring-color': themeColors.primary }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = themeColors.primary;
                    e.currentTarget.style.boxShadow = `0 0 0 2px ${themeColors.interactive.focusRing}`;
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#E5E7EB';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  placeholder="Partner's email address"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-2">Phone Number (Optional)</label>
                <input
                  type="tel"
                  value={partnerData.phoneNumber}
                  onChange={handlePartnerPhoneNumberChange}
                  className="w-full px-4 py-3 bg-white text-black border border-gray-200 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                  style={{ '--focus-ring-color': themeColors.primary }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = themeColors.primary;
                    e.currentTarget.style.boxShadow = `0 0 0 2px ${themeColors.interactive.focusRing}`;
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#E5E7EB';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  placeholder="(XXX) XXX-XXXX"
                />
            </div>
              
            <div>
                <label className="block text-sm font-medium text-gray-600 mb-2">Date of Birth (Optional)</label>
                <input
                  type="date"
                  value={partnerData.dateOfBirth}
                  onChange={(e) => setPartnerData({ ...partnerData, dateOfBirth: e.target.value })}
                  min={getMinDate()}
                  max={getMaxDate()}
                  className="w-full px-4 py-3 bg-white text-black border border-gray-200 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                  style={{ '--focus-ring-color': themeColors.primary }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = themeColors.primary;
                    e.currentTarget.style.boxShadow = `0 0 0 2px ${themeColors.interactive.focusRing}`;
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#E5E7EB';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                />
              </div>
              
              <div className="flex space-x-4 pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 py-3 px-4 bg-gradient-to-r from-[#6C5DD3] to-[#8247E5] text-white font-medium rounded-xl hover:opacity-90 transition duration-200 disabled:opacity-50"
                >
                  {loading ? 'Saving...' : (user?.isCreatedPartner ? 'Update Partner Profile' : 'Create Partner Profile')}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreatePartnerModal(false)}
                  className="py-3 px-4 bg-gradient-to-r from-[#6C5DD3] to-[#8247E5] text-white font-medium rounded-xl hover:opacity-90 transition duration-200"
                  disabled={loading}
                >
                  Cancel
                </button>
          </div>
            </form>
          </Dialog.Panel>
      </div>
      </Dialog>
  </>);
};

export default Profile; 